const STRIPE_PROVIDER = 'stripe';
const PAYPAL_PROVIDER = 'paypal';
const REVENUECAT_PROVIDER = 'revenuecat';
const ACTIVE_STATUS = 'active';

type SubscriptionClaim = {
  source?: { id?: string; provider?: string };
  status?: string;
};

/**
 * Provider values reach us from Auth0 with inconsistent casing (the app
 * normalises the same claim for the same reason), so every comparison here
 * lower-cases first.
 */
const normalise = (value: string | undefined) => value?.toLowerCase();

/**
 * The provider that describes the invoice on screen. The subscription response
 * wins when it has arrived, so the row can never be left expecting invoice data
 * that the endpoint has already declined to send; the session claim covers the
 * window before the request resolves. Both ultimately come from Auth0, but only
 * one of them comes back alongside the invoice being rendered.
 */
export const resolveBillingProvider = (
  subscription: SubscriptionClaim | undefined,
  invoiceProvider: string | undefined
): string | undefined =>
  normalise(invoiceProvider) ?? normalise(subscription?.source?.provider);

/** Only Stripe subscriptions have an invoice for the API to report. */
export const hasInvoiceBackedBilling = (
  provider: string | undefined
): boolean => normalise(provider) === STRIPE_PROVIDER;

export const isPayPalBilling = (provider: string | undefined): boolean =>
  normalise(provider) === PAYPAL_PROVIDER;

export const isRevenueCatBilling = (provider: string | undefined): boolean =>
  normalise(provider) === REVENUECAT_PROVIDER;

/**
 * Resolves whether the subscription has been cancelled, preferring the invoice
 * where one exists and falling back to the Auth0 subscription claim where one
 * cannot. Without the fallback a non-Stripe member never leaves the loading
 * state, because the flag they are waiting on is never sent (FFH-2453).
 *
 * Returns undefined only where the answer is genuinely unknown: a Stripe invoice
 * still in flight. A member with no subscription claim is a free member with
 * nothing to cancel, and a claim carrying no status is not a cancellation
 * either, so both read as active. Only an explicit non-active status cancels.
 */
export const resolveIsCancelled = ({
  provider,
  claimStatus,
  invoiceIsCancelled
}: {
  provider: string | undefined;
  claimStatus: string | undefined;
  invoiceIsCancelled: boolean | undefined;
}): boolean | undefined => {
  if (hasInvoiceBackedBilling(provider)) return invoiceIsCancelled;
  if (!claimStatus) return false;

  return normalise(claimStatus) !== ACTIVE_STATUS;
};
